import { useState } from "react";
import { authedFetch } from "../../lib/api-client";
import { useT } from "../../lib/use-t";
import { SegmentedControl } from "./SegmentedControl";

export type DuoModeDefaultToggleProps = {
  /** Initial value, server-rendered from `users.useDuoSkinDefault`. */
  initialValue: boolean;
};

/**
 * Sticky preference for "DUO real-exam mode by default".
 *
 * When ON, `/api/exam/start` reads this and seeds the new session's
 * skin to "duo" without the client having to pass an explicit flag.
 *
 * Visual: the shared Vertrouwd SegmentedControl pill (Aan/Uit), so the
 * /settings page reads as a sibling to the in-session toggles. Persists
 * optimistically — the round-trip stays out of the way unless it 5xxs.
 */
export function DuoModeDefaultToggle({ initialValue }: DuoModeDefaultToggleProps) {
  const t = useT();
  const [enabled, setEnabled] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function set(next: boolean) {
    if (pending || next === enabled) return;
    setError(null);
    setEnabled(next);
    setPending(true);
    try {
      const res = await authedFetch("/user/preferences", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ useDuoSkinDefault: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch {
      setEnabled(!next);
      setError(t("settings.duoModeSaveError"));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1.5">
      <SegmentedControl
        ariaLabel={t("settings.duoModeHeading")}
        value={enabled ? "on" : "off"}
        onChange={(v) => set(v === "on")}
        disabled={pending}
        options={[
          { value: "on", label: t("settings.duoModeOn") },
          { value: "off", label: t("settings.duoModeOff") },
        ]}
      />
      {error ? (
        <p role="alert" className="text-xs text-[var(--fail)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}
